import { OrderAction, OrderStatus } from "@prisma/client";
import { z } from "zod";

import { type TargetedShipmentForwardCommand, targetedShipmentForwardCommandSchema } from "~/shared/orders/targeted-shipment";
import { effectLocationTokenSchema } from "~/shared/types/EffectLocation";

import {
  OrderActionSubmitSchema,
  type OrderActionSubmitSchemaType,
  OrderActivityMarkDeliveredSchema,
  type OrderActivityMarkDeliveredSchemaType,
  OrderActivityShipInputSchema,
  OrderActivityShipSchema,
  type OrderActivityShipSchemaType,
  orderShipmentPlanSchema,
} from "./schema";

export const orderApprovalActionSchema = z.union([
  z.literal(OrderAction.SUBMIT),
  z.literal(OrderAction.SHIP),
  z.literal(OrderAction.MARK_DELIVERED),
]);
export type OrderApprovalAction = z.infer<typeof orderApprovalActionSchema>;

/** Persist the owner's instant, including milliseconds, without interpreting a picker a second time. */
const instantSchema = z
  .string()
  .datetime()
  .refine(value => {
    const date = new Date(value);
    return Number.isFinite(date.getTime()) && date.toISOString() === value;
  }, "Expected a canonical ISO instant.");

const submitItemSchema = OrderActionSubmitSchema.shape.items.element;
const submitLotSchema = submitItemSchema.shape.lots.element;
const decimalStringSchema = z.string().refine(value => {
  const parsed = submitLotSchema.shape.quantity.safeParse(value);
  return parsed.success && parsed.data.isFinite();
}, "Expected a finite positive decimal quantity.");

const persistedSubmitInputSchema = OrderActionSubmitSchema.extend({
  action: z.literal(OrderAction.SUBMIT),
  items: z
    .array(submitItemSchema.extend({ lots: z.array(submitLotSchema.extend({ quantity: decimalStringSchema }).strict()) }).strict())
    .min(1),
}).strict();

const platformSchema = orderShipmentPlanSchema.shape.shipmentPlan.shape.platforms.innerType().element;
const persistedShipmentPlanSchema = orderShipmentPlanSchema.shape.shipmentPlan
  .extend({
    platforms: z.array(platformSchema.extend({ items: z.array(platformSchema.shape.items.element.strict()) }).strict()),
  })
  .strict();

const persistedShipInputSchema = OrderActivityShipInputSchema.extend({
  action: z.literal(OrderAction.SHIP),
  roOrderedAt: instantSchema,
  shipBy: instantSchema.nullable(),
  requiredBy: instantSchema.nullable(),
  actualShipDate: instantSchema,
  poReceivedAt: instantSchema.nullable().optional(),
  // These values are materialized by the owner parse at capture time. A damaged payload must not
  // recover them from a fresh default or preprocess during execution.
  useCustomShippingInstructions: z.boolean(),
  grossShipmentWeightOverride: z.number().finite().positive().nullable(),
  palletCountOverride: z.number().int().min(0).nullable(),
  shipmentPlan: persistedShipmentPlanSchema,
}).strict();

const persistedDeliveryInputSchema = OrderActivityMarkDeliveredSchema.innerType()
  .extend({
    action: z.literal(OrderAction.MARK_DELIVERED),
    roOrderedAt: instantSchema,
    requiredBy: instantSchema.nullable(),
    deliveredOn: instantSchema,
  })
  .strict();

const payloadSchema = z.discriminatedUnion("action", [
  z.object({ version: z.literal(1), action: z.literal(OrderAction.SUBMIT), input: persistedSubmitInputSchema }).strict(),
  z.object({ version: z.literal(1), action: z.literal(OrderAction.SHIP), input: persistedShipInputSchema }).strict(),
  z.object({ version: z.literal(1), action: z.literal(OrderAction.MARK_DELIVERED), input: persistedDeliveryInputSchema }).strict(),
]);

export type OrderApprovalActionPayloadV1 = z.infer<typeof payloadSchema>;
type DecodedWholeOrderApprovalAction =
  | { action: typeof OrderAction.SUBMIT; input: OrderActionSubmitSchemaType }
  | { action: typeof OrderAction.SHIP; input: OrderActivityShipSchemaType }
  | { action: typeof OrderAction.MARK_DELIVERED; input: OrderActivityMarkDeliveredSchemaType };

function decodePayload(payload: OrderApprovalActionPayloadV1): DecodedWholeOrderApprovalAction {
  switch (payload.action) {
    case OrderAction.SUBMIT:
      return { action: payload.action, input: OrderActionSubmitSchema.parse(payload.input) };
    case OrderAction.SHIP:
      return {
        action: payload.action,
        input: OrderActivityShipSchema.parse({
          ...payload.input,
          roOrderedAt: new Date(payload.input.roOrderedAt),
          shipBy: payload.input.shipBy === null ? null : new Date(payload.input.shipBy),
          requiredBy: payload.input.requiredBy === null ? null : new Date(payload.input.requiredBy),
          actualShipDate: new Date(payload.input.actualShipDate),
          ...(payload.input.poReceivedAt === undefined
            ? {}
            : { poReceivedAt: payload.input.poReceivedAt === null ? null : new Date(payload.input.poReceivedAt) }),
        }),
      };
    case OrderAction.MARK_DELIVERED:
      return {
        action: payload.action,
        input: OrderActivityMarkDeliveredSchema.parse({
          ...payload.input,
          roOrderedAt: new Date(payload.input.roOrderedAt),
          requiredBy: payload.input.requiredBy === null ? null : new Date(payload.input.requiredBy),
          deliveredOn: new Date(payload.input.deliveredOn),
        }),
      };
  }
}

/** Strict JSON shape first, then the complete existing owner validation, including cross-field checks. */
export const orderApprovalActionPayloadV1Schema = payloadSchema.superRefine((payload, ctx) => {
  try {
    decodePayload(payload);
  } catch (error) {
    if (!(error instanceof z.ZodError)) {
      throw error;
    }
    for (const issue of error.issues) {
      ctx.addIssue({ ...issue, path: ["input", ...issue.path] });
    }
  }
});

export function encodeOrderApprovalActionPayload(action: OrderApprovalAction, input: unknown): OrderApprovalActionPayloadV1 {
  switch (orderApprovalActionSchema.parse(action)) {
    case OrderAction.SUBMIT: {
      const parsed = OrderActionSubmitSchema.parse(input);
      return orderApprovalActionPayloadV1Schema.parse({
        version: 1,
        action,
        input: {
          ...parsed,
          items: parsed.items.map(item => ({
            ...item,
            lots: item.lots.map(lot => ({ ...lot, quantity: lot.quantity.toString() })),
          })),
        },
      });
    }
    case OrderAction.SHIP: {
      const parsed = OrderActivityShipSchema.parse(input);
      return orderApprovalActionPayloadV1Schema.parse({
        version: 1,
        action,
        input: {
          ...parsed,
          roOrderedAt: parsed.roOrderedAt.toISOString(),
          shipBy: parsed.shipBy?.toISOString() ?? null,
          requiredBy: parsed.requiredBy?.toISOString() ?? null,
          actualShipDate: parsed.actualShipDate.toISOString(),
          ...(parsed.poReceivedAt === undefined
            ? {}
            : { poReceivedAt: parsed.poReceivedAt === null ? null : parsed.poReceivedAt.toISOString() }),
        },
      });
    }
    case OrderAction.MARK_DELIVERED: {
      const parsed = OrderActivityMarkDeliveredSchema.parse(input);
      return orderApprovalActionPayloadV1Schema.parse({
        version: 1,
        action,
        input: {
          ...parsed,
          roOrderedAt: parsed.roOrderedAt.toISOString(),
          requiredBy: parsed.requiredBy?.toISOString() ?? null,
          deliveredOn: parsed.deliveredOn.toISOString(),
        },
      });
    }
  }
}

/** Targeted actions use a new version; persisted whole-order v1 snapshots and hashes are unchanged. */
const persistTargetedCommand = (command: TargetedShipmentForwardCommand) => {
  if (command.action === "SHIP") {
    const { poReceivedAt, ...ship } = command;
    return {
      ...ship,
      occurredAt: command.occurredAt.toISOString(),
      ...(poReceivedAt === undefined ? {} : { poReceivedAt: poReceivedAt.toISOString() }),
    };
  }
  return { ...command, occurredAt: command.occurredAt.toISOString() };
};

const persistedTargetedCommandSchema = z.unknown().transform((value, ctx) => {
  const dates = z.object({ occurredAt: instantSchema, poReceivedAt: instantSchema.optional() }).passthrough().safeParse(value);
  if (!dates.success) {
    for (const issue of dates.error.issues) {
      ctx.addIssue(issue);
    }
    return z.NEVER;
  }
  const parsed = targetedShipmentForwardCommandSchema.safeParse({
    ...dates.data,
    occurredAt: new Date(dates.data.occurredAt),
    ...(dates.data.poReceivedAt === undefined ? {} : { poReceivedAt: new Date(dates.data.poReceivedAt) }),
  });
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      ctx.addIssue(issue);
    }
    return z.NEVER;
  }
  return persistTargetedCommand(parsed.data);
});

export const orderApprovalActionPayloadV2Schema = z
  .object({
    version: z.literal(2),
    mode: z.literal("TARGETED"),
    action: z.union([z.literal(OrderAction.SHIP), z.literal(OrderAction.MARK_DELIVERED)]),
    // This is captured from the locked owner, never accepted as preview/request input.
    fromStatus: z.union([z.literal(OrderStatus.CONFIRMED), z.literal(OrderStatus.SHIPPED)]),
    input: persistedTargetedCommandSchema,
  })
  .strict()
  .superRefine((payload, ctx) => {
    if ((payload.action === OrderAction.SHIP) !== (payload.input.action === "SHIP")) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["action"], message: "Targeted approval action does not match its command." });
    }
  });

export type OrderApprovalActionPayloadV2 = z.infer<typeof orderApprovalActionPayloadV2Schema>;
export type OrderApprovalActionPayload = OrderApprovalActionPayloadV1 | OrderApprovalActionPayloadV2;
export const orderApprovalActionPayloadSchema = z.union([orderApprovalActionPayloadV1Schema, orderApprovalActionPayloadV2Schema]);

export type DecodedTargetedOrderApprovalAction = {
  action: "TARGETED";
  ownerAction: typeof OrderAction.SHIP | typeof OrderAction.MARK_DELIVERED;
  fromStatus: typeof OrderStatus.CONFIRMED | typeof OrderStatus.SHIPPED;
  input: TargetedShipmentForwardCommand;
};
export type DecodedOrderApprovalAction = DecodedWholeOrderApprovalAction | DecodedTargetedOrderApprovalAction;

export function encodeTargetedOrderApprovalActionPayload(input: unknown, fromStatus: OrderStatus): OrderApprovalActionPayloadV2 {
  const parsed = targetedShipmentForwardCommandSchema.parse(input);
  const normalized =
    parsed.action === "SHIP"
      ? {
          ...parsed,
          selections: parsed.selections
            .map(selection => ({
              ...selection,
              ...(selection.locationSources === undefined
                ? {}
                : {
                    locationSources: [...selection.locationSources].sort((left, right) =>
                      left.warehouseLocationId.localeCompare(right.warehouseLocationId),
                    ),
                  }),
            }))
            .sort((left, right) => left.orderItemId.localeCompare(right.orderItemId)),
        }
      : {
          ...parsed,
          selections: [...parsed.selections].sort((left, right) => left.fulfillmentLineId.localeCompare(right.fulfillmentLineId)),
        };
  return orderApprovalActionPayloadV2Schema.parse({
    version: 2,
    mode: "TARGETED",
    action: normalized.action === "SHIP" ? OrderAction.SHIP : OrderAction.MARK_DELIVERED,
    fromStatus,
    input: persistTargetedCommand(normalized),
  });
}

export function decodeOrderApprovalActionPayload(value: unknown): DecodedOrderApprovalAction {
  const payload = orderApprovalActionPayloadSchema.parse(value);
  if (payload.version === 1) {
    return decodePayload(payload);
  }
  return {
    action: "TARGETED",
    ownerAction: payload.action,
    fromStatus: payload.fromStatus,
    input: targetedShipmentForwardCommandSchema.parse({
      ...payload.input,
      occurredAt: new Date(payload.input.occurredAt),
      ...(payload.input.action !== "SHIP" || payload.input.poReceivedAt === undefined
        ? {}
        : { poReceivedAt: new Date(payload.input.poReceivedAt) }),
    }),
  };
}

export function encodeDecodedOrderApprovalAction(action: DecodedOrderApprovalAction): OrderApprovalActionPayload {
  return action.action === "TARGETED"
    ? encodeTargetedOrderApprovalActionPayload(action.input, action.fromStatus)
    : encodeOrderApprovalActionPayload(action.action, action.input);
}

/** Regulatory destination is intent; a partial operation need not advance the whole header to it. */
export function getOrderApprovalPayloadTransition(payload: OrderApprovalActionPayload) {
  const transition = getOrderApprovalActionTransition(payload.action);
  return payload.version === 1 ? transition : { ...transition, fromStatus: payload.fromStatus };
}

export function getDecodedOrderApprovalTransition(action: DecodedOrderApprovalAction) {
  return getOrderApprovalPayloadTransition(encodeDecodedOrderApprovalAction(action));
}

/** The closed action/location contract; callers still validate live state and authority under owner locks. */
export function getOrderApprovalActionTransition(action: OrderApprovalAction) {
  switch (orderApprovalActionSchema.parse(action)) {
    case OrderAction.SUBMIT:
      return {
        location: effectLocationTokenSchema.parse("sales_order.status_transition.draft_to_confirmed"),
        fromStatus: OrderStatus.DRAFT,
        toStatus: OrderStatus.CONFIRMED,
      };
    case OrderAction.SHIP:
      return {
        location: effectLocationTokenSchema.parse("sales_order.status_transition.confirmed_to_shipped"),
        fromStatus: OrderStatus.CONFIRMED,
        toStatus: OrderStatus.SHIPPED,
      };
    case OrderAction.MARK_DELIVERED:
      return {
        location: effectLocationTokenSchema.parse("sales_order.status_transition.shipped_to_delivered"),
        fromStatus: OrderStatus.SHIPPED,
        toStatus: OrderStatus.DELIVERED,
      };
  }
}
